import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/client/components/ui/card';
import { Badge } from '@/client/components/ui/badge';
import { Webhook, CheckCircle, XCircle, Key, KeyRound, Copy, Check } from 'lucide-react';
import type { ClientWebhook } from '@shared/types';

interface WebhooksCardProps {
  webhooks: ClientWebhook[];
}

// Truncate URL for display
function truncateUrl(url: string, maxLength = 35): string {
  if (url.length <= maxLength) return url;
  return url.substring(0, maxLength) + '...';
}

// Get auth type display
function getAuthDisplay(authType: string): { label: string; icon: typeof Key } {
  switch (authType.toLowerCase()) {
    case 'basic':
      return { label: 'Basic', icon: KeyRound };
    case 'bearer':
      return { label: 'Bearer', icon: Key };
    default:
      return { label: authType, icon: Key };
  }
}

function CopyableUrl({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1 truncate font-mono text-xs hover:text-primary transition-colors cursor-pointer text-left"
      title={`Click to copy: ${url}`}
    >
      <span className="truncate">{truncateUrl(url)}</span>
      {copied ? (
        <Check className="h-3 w-3 text-green-600 flex-shrink-0" />
      ) : (
        <Copy className="h-3 w-3 text-muted-foreground flex-shrink-0 opacity-0 group-hover:opacity-100" />
      )}
    </button>
  );
}

export function WebhooksCard({ webhooks }: WebhooksCardProps) {
  if (webhooks.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Webhook className="h-4 w-4" />
            Webhooks
          </CardTitle>
          <Badge variant="secondary">{webhooks.length}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {webhooks.map((webhook) => {
            const authDisplay = webhook.auth ? getAuthDisplay(webhook.auth.authtype) : null;
            const AuthIcon = authDisplay?.icon ?? Key;

            return (
              <div
                key={webhook.webhookIdx}
                className="group flex items-center justify-between gap-2 text-sm"
              >
                <div className="min-w-0 flex-1">
                  <CopyableUrl url={webhook.callback} />
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {authDisplay ? (
                    <Badge variant="outline" className="text-xs gap-1">
                      <AuthIcon className="h-3 w-3" />
                      {authDisplay.label}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs text-muted-foreground">
                      None
                    </Badge>
                  )}

                  {webhook.upNow ? (
                    <Badge variant="default" className="text-xs gap-1 bg-green-600">
                      <CheckCircle className="h-3 w-3" />
                      Active
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs gap-1">
                      <XCircle className="h-3 w-3" />
                      Inactive
                    </Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
